import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { products } from '../data/content';

const Products = () => {
  const [filter, setFilter] = useState('All');
  const categories = ['All', 'Yarns', 'Cords', 'Felts', 'Ropes', 'Accessories'];

  const filteredProducts = filter === 'All' 
    ? products 
    : products.filter(p => p.category === filter);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-gradient-to-br from-teal-50/50 via-white to-orange-50/30">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div className="max-w-3xl" {...fadeInUp}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6">
              Complete Range of <span className="text-primary">FIBC Essentials</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              From polypropylene yarns to accessories, we provide everything you need for 
              FIBC manufacturing. All products available ex-stock with fast delivery.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          {/* Filter */}
          <motion.div className="flex flex-wrap gap-2 mb-12 justify-center" {...fadeInUp}>
            {categories.map((category) => (
              <Button
                key={category}
                variant={filter === category ? 'default' : 'outline'}
                onClick={() => setFilter(category)}
                size="sm"
              >
                {category}
              </Button>
            ))}
          </motion.div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product, idx) => (
              <motion.div
                key={product.id}
                {...fadeInUp}
                transition={{ delay: idx * 0.05 }}
              >
                <Link to={`/products/${product.id}`}>
                  <Card className="overflow-hidden h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-200" data-testid={`product-card-${product.id}`}>
                    <div className="aspect-video overflow-hidden bg-muted">
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardHeader>
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-lg">{product.name}</CardTitle>
                        <Badge variant="secondary">{product.category}</Badge>
                      </div>
                      <CardDescription className="line-clamp-2">
                        {product.shortDescription}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex flex-wrap gap-2">
                          {product.specs.map((spec, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {spec}
                            </Badge>
                          ))}
                        </div>
                        <Button variant="outline" className="w-full" data-testid="product-card-view-details">
                          View Details
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No products found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <motion.div className="max-w-2xl mx-auto space-y-6" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl font-semibold">Can't Find What You Need?</h2>
            <p className="text-lg text-muted-foreground">
              We offer custom solutions for specific requirements. Contact us to discuss your needs.
            </p>
            <Link to="/contact">
              <Button size="lg">
                Contact Us
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Products;
